/**
 * One mark per step type, drawn from the act itself.
 *
 * Monochrome on purpose: colour on this surface already means blocking, warning and clear,
 * and a second colour system would make an author read two languages at once.
 *
 * 2026-10-28 (owner) · lucide icons, the set the rest of the app draws with, in place of the
 * hand-drawn paths: a bare tick for Readiness and a Σ for Calculation did not read as the act.
 * Shown with the type's name in the step's type tag (SequenceView), and on the Flow tiles.
 */
import React from "react";
import {
  ArrowRightLeft, Calculator, CirclePlay, ClipboardCheck, Droplets, FileText, FlaskConical, Gauge, Hash,
  Heading, Hourglass, PackagePlus, Repeat, Scale, SearchCheck, Signature, SlidersHorizontal, Split,
  SprayCan, TestTube, Eye, Weight,
} from "lucide-react";

const ICONS = {
  READINESS: ClipboardCheck,          // preconditions, equipment precheck
  CLEARANCE: SearchCheck,             // line, area, room — looked over and found clear
  CLEANING: SprayCan,
  DISPENSING: Weight,                 // weighed out to the bill's quantity
  MATERIAL_CHARGE: PackagePlus,       // a material added to the batch
  CONTROLLED_ADDITION: Droplets,      // added at a rate
  TRANSFER: ArrowRightLeft,
  EQUIPMENT_SETPOINT: SlidersHorizontal,
  EQUIPMENT_RUN: CirclePlay,         // the cog is a machine chip's mark; a run is started
  PARAMETER_RECORD: Gauge,
  SAMPLE: TestTube,
  AQL_SAMPLE: TestTube,
  ENDPOINT_TEST: FlaskConical,
  INSPECTION: Eye,
  VISUAL_INSPECTION: Eye,
  COUNT: Hash,
  CALCULATION: Calculator,
  BALANCE: Scale,                     // reconciliation: what went in against what came out
  HOLD: Hourglass,
  DOCUMENT: FileText,
  SIGN: Signature,
  DECISION: Split,
  LOOP: Repeat,
  HEADING: Heading,
};

/** 2026-10-28 (owner) · each type's own colour, for its tag beside the step's name. Dark enough
 *  to read as text on its own light tint; the row's left edge keeps the five family colours. */
export const STEP_COLOR = {
  READINESS: "#b45309", CLEARANCE: "#c2410c", CLEANING: "#0e7490",
  DISPENSING: "#0f766e", MATERIAL_CHARGE: "#047857", CONTROLLED_ADDITION: "#0369a1", TRANSFER: "#4d7c0f",
  EQUIPMENT_SETPOINT: "#4338ca", EQUIPMENT_RUN: "#475569",
  PARAMETER_RECORD: "#1d4ed8", SAMPLE: "#be185d", AQL_SAMPLE: "#be185d", ENDPOINT_TEST: "#a21caf", INSPECTION: "#be123c", VISUAL_INSPECTION: "#be123c", COUNT: "#6d28d9",
  CALCULATION: "#9a3412", BALANCE: "#a16207", HOLD: "#7e22ce", DOCUMENT: "#57534e", SIGN: "#0c4a6e",
  DECISION: "#7c3aed", LOOP: "#7c3aed", HEADING: "#475569",
};

export default function StepIcon({ kind, size = 14 }) {
  const Icon = ICONS[kind];
  if (!Icon) return null;
  return <Icon className="dz-ic dz-lic" size={size} strokeWidth={2} aria-hidden="true" />;
}
